import type { StyleClass } from '@disy/lib/add-style-class';
import { addStyleClass } from '@disy/lib/add-style-class';
import type { Content } from '@disy/ui/content';
import { normalizeContent } from '@disy/ui/content';
import { LabelWithIcon } from '@disy/ui/label-with-icon';

import type { AttributeType } from 'cadenza/repository/model/attribute-type.gen';
import { isAttributeType } from 'cadenza/repository/model/attribute-type.gen';
import type { HierarchyType } from 'cadenza/repository/model/hierarchy-type.gen';
import type { AttributeTypeData } from 'cadenza/workbook/attribute-element/data-type-icon';
import { dataTypeIconElements } from 'cadenza/workbook/attribute-element/data-type-icon';
import dataTypeIconStyles from 'cadenza/workbook/attribute-element/data-type-icon.css' with { type: 'css' };

import styles from './attribute-label.module.css' with { type: 'css' };

function isHierarchyType(value: string): value is HierarchyType {
  return value === 'DATE_HIERARCHY' || value === 'MULTI_ATTRIBUTE_HIERARCHY';
}

const COMPONENT_NAME = 'd-attribute-label';

export function createAttributeLabel(
  attributeOrHierarchyType: AttributeType | HierarchyType,
  name: Content,
  { styleClass, ...attribute }: Partial<AttributeTypeData> & { styleClass?: StyleClass } = {},
) {
  let label: AttributeLabel;
  if (isAttributeType(attributeOrHierarchyType)) {
    label = new AttributeLabel({ type: attributeOrHierarchyType, ...attribute });
  } else {
    label = new AttributeLabel({ hierarchyType: attributeOrHierarchyType, ...attribute });
  }
  label.append(...normalizeContent(name));
  addStyleClass(label, styleClass);
  return label;
}

export class AttributeLabel extends LabelWithIcon {
  constructor(attribute?: AttributeTypeData) {
    super(() => dataTypeIconElements(attribute ?? this._attributeFromAttributes));
    this.shadowRoot.adoptedStyleSheets = [...this.shadowRoot.adoptedStyleSheets, dataTypeIconStyles, styles];
  }

  static get observedAttributes() {
    return ['attribute-type'];
  }

  attributeChangedCallback() {
    super.render();
  }

  get _attributeFromAttributes(): AttributeTypeData {
    const type = this.getAttribute('attribute-type');
    const hasGeometryRelation = this.getAttribute('has-geometry-relation') === 'true';
    if (type != null && isAttributeType(type)) {
      return { type, hasGeometryRelation };
    }
    if (type != null && isHierarchyType(type)) {
      return { hierarchyType: type, hasGeometryRelation };
    }
    throw new Error(`Invalid attribute type "${type}"`);
  }
}

customElements.define(COMPONENT_NAME, AttributeLabel);
